import React, { useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../contexts/AuthContext/AuthContext";

const SettingsPage = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const { user, authTokens, login, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const refreshToken = async () => {
    if (!authTokens?.refresh) {
      logout();
      navigate("/signin");
      return null;
    }
    try {
      const response = await axios.post(`${SERVER_URL}/api/auth/jwt/refresh/`, {
        refresh: authTokens.refresh,
      });
      login(user, response.data.access, authTokens.refresh);
      return response.data.access;
    } catch (err) {
      console.error("Token refresh failed:", err);
      logout();
      navigate("/signin");
      return null;
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    if (!authTokens?.access) {
      setError("You are not authenticated. Please sign in.");
      navigate("/signin");
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      setIsLoading(false);
      return;
    }

    let token = authTokens.access;
    try {
      await axios.post(
        `${SERVER_URL}/auth/users/set_password/`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      if (err.response?.status === 401) {
        token = await refreshToken();
        if (token) {
          try {
            await axios.post(
              `${SERVER_URL}/auth/users/set_password/`,
              {
                current_password: currentPassword,
                new_password: newPassword,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccess("Password changed successfully.");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
          } catch (retryErr) {
            setError(
              retryErr.response?.data?.current_password?.[0] ||
                retryErr.response?.data?.new_password?.[0] ||
                retryErr.response?.data?.detail ||
                "Failed to change password after token refresh."
            );
          }
        }
      } else {
        setError(
          err.response?.data?.current_password?.[0] ||
            err.response?.data?.new_password?.[0] ||
            err.response?.data?.detail ||
            "Failed to change password. Please check your current password."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!user || !authTokens) {
    navigate("/signin");
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-0">
      <Helmet>
        <title>Settings – LingoLab</title>
        <meta
          name="description"
          content="Change your LingoLab account password securely. Update your credentials and keep your account safe."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://lingolab.ca/settings" />
        <meta property="og:title" content="Settings – LingoLab" />
        <meta
          property="og:description"
          content="Change your LingoLab account password securely. Update your credentials and keep your account safe."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/settings" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="Settings – LingoLab" />
        <meta
          name="twitter:description"
          content="Change your LingoLab account password securely. Update your credentials and keep your account safe."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-full sm:max-w-md">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
          Change Password
        </h2>
        <form onSubmit={handlePasswordChange}>
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="currentPassword"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              Current Password
            </label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoFocus
              autoComplete="current-password"
            />
          </div>
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="newPassword"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoComplete="new-password"
            />
          </div>
          <div className="mb-4 sm:mb-6">
            <label
              htmlFor="confirmPassword"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoComplete="new-password"
            />
          </div>
          {error && (
            <p
              className="text-red-500 text-sm sm:text-base mb-3 sm:mb-4 break-words"
              aria-live="polite"
            >
              {error}
            </p>
          )}
          {success && (
            <p
              className="text-green-500 text-sm sm:text-base mb-3 sm:mb-4 break-words"
              aria-live="polite"
            >
              {success}
            </p>
          )}
          <button
            type="submit"
            className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50 text-base"
            disabled={isLoading}
          >
            {isLoading ? "Changing..." : "Change Password"}
          </button>
        </form>
        <div className="mt-3 sm:mt-4 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-blue-500 text-sm sm:text-base font-semibold hover:underline p-2"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
